'use client';
import { useSearchParams } from 'next/navigation';

import BonusPeriod from './components/BonusPeriod/BonusPeriod';
import ExtensionDetails from './components/ExtensionDetails/ExtensionDetails';
import { UrlParamKeys } from '@/common/utils/constants';
import { useMembershipDates } from './MembershipTypeSection.service';

import ReceiptAccordion from '../common/ReceiptAccordion/ReceiptAccordion';
import { ReceiptDevider } from '../common/ReceiptDevider/ReceiptDevider';
import TermDuration from './components/TermDuration/TermDuration';
import TermInformation from './components/TermInformation/TermInformation';
import TimeBasedVoucherPeriod from './components/TimeBasedVoucherPeriod/TimeBasedVoucherPeriod';
import { MembershipTypeSectionProps } from './MembershipTypeSection.types';
import { ProratedSection } from './components/ProratedSection/ProratedSection';

export default function MembershipTypeSection({
  currency,
  priceDecimalSeparator = '.',
  lang,
  totalPrice,
  oldPrice,
  isOpen,
  isExtending,
  termData,
  voucherData,
  customerExtensionEndDate,
  translations,
  showPriceAfterInitialTerm,
  isLoadingPrice,
  totalProratedPrice,
  oldProratedPrice,
}: MembershipTypeSectionProps) {

  const {
    startDate,
    rowMembershipPeriodDate,
    isToday,
    preuseDate,
  } = useMembershipDates({ isExtending, termData, customerExtensionEndDate, translations });

  const searchParams = useSearchParams();

  const isProratedShown = !isExtending && (Number(totalProratedPrice) >= 0 && !isToday)

  if (!termData) return;

  const isTimeBasedVoucher =
    voucherData?.voucher_effective_period === 'TIME_BASED';
  const hasBonusPeriod = Boolean(
    voucherData?.voucher_bonus_period || termData?.rateBonusPeriods?.length
  );

  const title = `${searchParams.get(UrlParamKeys.AccessLevelName)} ${searchParams.get(UrlParamKeys.TermName) || `${termData?.term?.term} ${termData?.term?.termUnit}`}`;

  return (
    <>
      <ReceiptDevider />
      <ReceiptAccordion
        expanded={isOpen}
        currency={currency}
        title={title}
        price={String(totalPrice)}
        oldPrice={oldPrice}
        loading={isLoadingPrice}
      >
        <TermInformation
          lang={lang}
          translations={translations}
          startDate={startDate}
          durationPeriod={`${termData?.termValue} ${termData?.termUnit}`}
        />
        {isProratedShown && (
          <ProratedSection
            translations={translations}
            currency={currency}
            oldProratedPrice={oldProratedPrice}
            selectedStartDate={preuseDate}
            lang={lang}
            totalProratedPrice={totalProratedPrice}
          />
        )}
        {hasBonusPeriod && (
          <BonusPeriod
            translations={translations}
            currency={currency}
            priceDecimalSeparator={priceDecimalSeparator}
            termData={termData}
            voucherData={voucherData}
            lang={lang}
            rowMembershipPeriodDate={rowMembershipPeriodDate}
            startDate={startDate}
          />
        )}
        {isTimeBasedVoucher && (
          <TimeBasedVoucherPeriod
            translations={translations}
            termData={termData}
            voucherData={voucherData}
            currency={currency}
            priceDecimalSeparator={priceDecimalSeparator}
            lang={lang}
            startDate={startDate}
          />
        )}
        <TermDuration
          translations={translations}
          termData={termData}
          voucherData={voucherData}
          currency={currency}
          priceDecimalSeparator={priceDecimalSeparator}
          lang={lang}
          startDate={startDate}
        />
        {showPriceAfterInitialTerm && <ExtensionDetails
          translations={translations}
          termData={termData}
          voucherData={voucherData}
          currency={currency}
          priceDecimalSeparator={priceDecimalSeparator}
          startDate={startDate}
          lang={lang}
        />}
      </ReceiptAccordion>
    </>
  );
}
